import {AfterContentInit, Component, ContentChild, ElementRef, EventEmitter, HostBinding, Input, Optional, Output, Renderer2, ViewChild} from '@angular/core';
import {AbstractControl, FormControlName, ValidationErrors} from '@angular/forms';
import {LabelDirective} from '../label/label.directive';
import {FormHintDirective} from '../hint/hint.directive';
import {TranslateService} from "@ngx-translate/core";
import {InputType} from '../root/input-type';
import {RadioComponent} from '../radio/radio.component';
import {CheckboxGroupComponent} from '../checkbox-group/checkbox-group.component';
import {SvgIcon} from '@valg-shared/utils/svg-icon';
import {InputDropdownComponent} from "../input-dropdown/input-dropdown.component";
import {ComboBoxComponent} from "../input-dropdown/combo-box/combo-box.component";
import {DatepickerComponent} from '../datepicker/datepicker.component';
import {SingleEventSemaphore} from "@valg-shared/utils/single-event-semaphore";
import {InputFileComponent} from "../file/input-file.component";
import {DomainValidationResultAnnouncerDirective, FieldValidationListener} from '@valg-shared/directives/domain-validation-result-announcer.directive';
import {StatusCode} from '@valg-shared/models/status-code.model';
import { FormPasswordComponent } from '../password/password.component';
import { MultiInputFileComponent } from '../multi-file/multi-input-file.component';

let NEXT_ID = 0;

@Component({
  selector: 'valg-input-group',
  templateUrl: './input-group.component.html'
})
export class InputGroupComponent implements AfterContentInit, FieldValidationListener {
  public id: string = 'valg-input-group-' + NEXT_ID++;

  @ContentChild(LabelDirective) public label!: LabelDirective;
  @ContentChild(InputType, { static: false }) public genericInput!: InputType;
  @ContentChild(InputDropdownComponent) public dropdownInput!: InputDropdownComponent<unknown>;
  @ContentChild(ComboBoxComponent) public comboBoxInput!: ComboBoxComponent;
  @ContentChild(RadioComponent) public radio!: RadioComponent;
  @ContentChild(CheckboxGroupComponent) public checkbox!: CheckboxGroupComponent;
  @ContentChild(DatepickerComponent) public datepicker!: DatepickerComponent;
  @ContentChild(FormPasswordComponent) public password!: FormPasswordComponent;
  @ContentChild(InputFileComponent) public inputFile!: InputFileComponent;
  @ContentChild(MultiInputFileComponent) public multiInputFile!: MultiInputFileComponent;
  @ContentChild(FormHintDirective) public hint!: FormHintDirective;

  @Output()
  public onInputFocusOut: EventEmitter<any> = new EventEmitter<any>()
  @Output()
  public onClear: EventEmitter<any> = new EventEmitter<any>()

  readonly SvgIcon = SvgIcon;
  @ViewChild('searchButton') searchButton!: ElementRef;
  @ViewChild('fieldset') fieldset!: ElementRef;
  public showSearchButton = false;

  public componentElement!: CheckboxGroupComponent | RadioComponent | InputDropdownComponent<unknown> | ComboBoxComponent | InputFileComponent | MultiInputFileComponent;
  public inputElement!: InputType;
  public formControl!: AbstractControl | null;
  public errorKey: string | undefined;
  public feedbackId = "";
  public errorMessage: string = "";
  public internalId = "";
  @Input() legend: string = "";
  @Input() tooltip!: string;
  @Input() secondary = false;
  @Input() excludedDomainErrorReasons: string[] = [];
  @Input() excludedFormErrors: string[] = [];
  @Input() enableClear: boolean = false;
  @Input() hideFeedback = false;
  @Input() alwaysUpdateErrorMessages = false;
  @HostBinding('class.compact')
  @Input() compact = false;
  @Input() maxWidth = false;

  public alertMessage = false;
  private singleEventSemaphore = new SingleEventSemaphore();
  public statusCode?: StatusCode;
  public reasons: string[] = [];
  private translation_key?: string;
  

  public alwaysDisplayStatusCode = false;

  constructor(
    protected translate: TranslateService,
    protected renderer: Renderer2,
    @Optional() protected validationAnnouncer: DomainValidationResultAnnouncerDirective,
  ) {
  }

  ngAfterViewInit(): void {
    this.validationAnnouncer?.registerFieldValidationListener(this);
  }
  ngOnDestroy() {
    this.validationAnnouncer?.deregisterFieldValidationListener(this);
  }

  reset(): void {
    this.errorMessage = "";
    this.statusCode = undefined;
    this.alertMessage = false;
    this.errorMessage = "";
  }

  resetStatusCodes(): void {
    this.statusCode = undefined;
    this.alertMessage = false;
  }

  updateStatusCodes(statusCode: StatusCode, forceDisplay = false) {
    if (this.shouldDisplayStatusCodes() || forceDisplay) {
      if (this.reasons.includes(statusCode.reason)) {
        if (!this.excludedDomainErrorReasons.includes(statusCode.reason)) {
          this.statusCode = statusCode;
          this.updateAriaDescribedBy(true);
        }
      }
    } else {
      this.reset();
      this.updateAriaDescribedBy(false);
    }
  }

  public updateFormErrors(dirtyCheck: boolean) {
    if (dirtyCheck && !this.isDirty()) {
      return;
    }
    if (!dirtyCheck && this.formControl?.errors) {
      this.formControl?.markAsTouched();
      this.formControl?.updateValueAndValidity();
    }
    if (this.formControl?.errors) {
      this.updateErrorMessage(this.formControl.errors);
    } else {
      this.removeErrorMessage();
    }
  }
  shouldDisplayStatusCodes() {
    return this.alwaysDisplayStatusCode || this.isDirty()
  }

  protected removeErrorMessage() {
    this.errorMessage = "";
    this.alertMessage = false;
    if (this.translation_key != null) {
      this.validationAnnouncer?.removeFormErrorKey(this.translation_key);
    }
    this.updateAriaDescribedBy(false);
  }



  protected updateErrorMessage(
    validationErrors: ValidationErrors,
    errorKey: string | null = null
  ) {
    const key = Object.keys(validationErrors)[0]
    const param = Object.values(validationErrors)[0]
    const translation_key = errorKey
      ? errorKey + key
      : `${this.errorKey}.${key}`;

    if (this.translation_key != null && !this.excludedFormErrors.includes(key)) {
      this.validationAnnouncer?.removeFormErrorKey(this.translation_key);
    }
    this.translation_key = translation_key;
    this.errorMessage = this.translation_key;
    if (!this.excludedFormErrors.includes(key)) {
      this.validationAnnouncer?.pushFormErrorKey(this.translation_key);
    }
    if(typeof param === "object"){
      this.validationAnnouncer?.setErrorParam(param, this.translation_key);
    }
    this.updateAriaDescribedBy(true);
  }

  protected getErrorParam(errorKey: string){
    return this.validationAnnouncer?.getErrorParam(errorKey);
  }

  public ngAfterContentInit(): void {
    if (this.genericInput) {
      this.genericInputAfterContentInit()
    } else {
      this.notGenericInputAfterContentInit()
    }
    if (this.dropdownInput) {
      this.internalId = this.dropdownInput.id
      this.dropdownInput.onBlurEvent.subscribe(async () => {
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit();
            resolve();
          })
        })
      });
      this.dropdownInput.dropdownMenuClose.subscribe(async () => {
        this.updateErrorMessages();
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit();
            resolve();
          })
        })
      });
      this.dropdownInput.onTabAwayEvent.subscribe(async () => {
        this.updateErrorMessages();
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit();
            resolve();
          })
        })
      });
    }
    if (this.comboBoxInput) {
      this.internalId = this.comboBoxInput.id
      this.comboBoxInput.onBlurEvent.subscribe(async () => {
        this.updateErrorMessages();
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit();
            resolve();
          })
        })
      });

      this.errorKey = this.comboBoxInput.errorKey;
    }
    if (this.radio) {
      this.internalId = this.radio.id
      this.radio.changeEvent.subscribe(async () => {
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit();
            resolve();
          })
        })
      })
    }

    if (this.checkbox) {
      this.internalId = this.checkbox.id;
      this.checkbox.changeEvent.subscribe(async () => {
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit();
            resolve();
          })
        })
      })
    }

    if (this.inputFile) {
      this.internalId = this.inputFile.id
      this.formControl?.statusChanges.subscribe(() => {
        this.updateErrorMessages();
      })
    }

    if (this.multiInputFile) {
      this.internalId = this.multiInputFile.id
      this.formControl?.statusChanges.subscribe(() => {
        this.updateErrorMessages();
      })
    }

    if (this.label) {
      this.labelInputAfterContentInit()
    }

   this.updateInputErrorMessageWhenValueChange()

    if (this.inputElement && this.searchButton
      && this.inputElement.getAttribute('readonly') !== null) {
      this.renderer.setAttribute(this.searchButton.nativeElement, 'disabled', 'true');
    }

    if (this.internalId) {
      this.feedbackId = this.internalId + '__feedback';
    }

    if (this.feedbackId === "") {
      this.feedbackId = `valg-fieldset-group-${NEXT_ID++}__feedback`;
    }

    this.setAttributesForHint();
  }

  private genericInputAfterContentInit(): void {
    this.inputElement = this.genericInput;
    this.internalId = this.inputElement.getAttribute('id');
    this.formControl = this.inputElement.getFormControl();
    this.errorKey = this.inputElement.errorKey;
    this.renderer.listen(this.genericInput.getElementRef().nativeElement, "keydown",
      (evt) => {
        if (evt.key === "Enter") {
          this.updateErrorMessages()
        }
      })
    if (this.formControl instanceof FormControlName) {
      this.formControl = this.formControl.control as AbstractControl;
    }
    if(this.compact){
      this.renderer.addClass(this.inputElement.getElementRef().nativeElement, 'valg-input__compact');
    }
    if(this.maxWidth){
      this.renderer.addClass(this.inputElement.getElementRef().nativeElement, 'valg-input__max-width');
    }
    this.genericInputFocusOutAfterContentInit()
  }

  private notGenericInputAfterContentInit(): void {
    this.componentElement = this.checkbox || this.radio || this.comboBoxInput || this.dropdownInput || this.inputFile || this.multiInputFile;
      if (this.componentElement) {
        /** Fetching the formControlName to be able to access control **/
        this.formControl = this.componentElement.formControl?.control;

        this.errorKey = this.componentElement.errorKey;
      }
  }

  private genericInputFocusOutAfterContentInit():void {
    this.inputElement.focusOut.subscribe(async (_result: FocusEvent | undefined) => {
      let updateMessages = true;
      if (_result !== undefined) {
        let nextFocus = _result.relatedTarget as Element;
        if (this.datepicker && nextFocus && nextFocus.classList.contains('valg-datepicker-button')
          && this.formControl?.value == "") {
          updateMessages = false;
        }
        if(this.password && nextFocus.classList.contains('valg-form-passowrd__button')){
          updateMessages = false;
        }
      }
      if (updateMessages && this.inputElement.getAttribute('readonly') !== "") {
        this.updateErrorMessages();
        await this.singleEventSemaphore.use(() => {
          return new Promise<void>((resolve) => {
            this.onInputFocusOut.emit(this.formControl?.value)
            resolve();
          })
        })
      }
    });
  }

  private labelInputAfterContentInit():void{
    if (this.dropdownInput) {
      this.dropdownInput.dropdownLabel = this.label.id;
    } else if (this.comboBoxInput) {
      this.comboBoxInput.dropdownLabel = this.label.id;
    }
    if ((!this.dropdownInput) || this.comboBoxInput) {
      this.label.setAttribute({ name: 'for', value: this.internalId });
    }
  }

  private updateInputErrorMessageWhenValueChange():void{
    this.formControl?.valueChanges.subscribe((_value) => {
      if (this.checkbox || this.radio || this.inputFile || this.alwaysUpdateErrorMessages || this.multiInputFile) {
        this.updateErrorMessages();
      }
    });
  }

  setAttributesForHint(){
    let element = this.getFormElement();
    if (element && this.hint) {
      this.renderer.setAttribute(element.nativeElement, 'aria-describedby', this.hint.id)
    }
  }

  get isClearAvailable(): boolean {
    return this.inputElement.getElementRef().nativeElement.value != '';
  }

  public isDirty() {
    return (this.formControl?.dirty || this.formControl?.touched) ?? false;
  }

  performSearch() {
    this.inputElement.performSubmit();
  }

  clearSearch() {
    this.inputElement.getElementRef().nativeElement.value = '';
    this.formControl?.reset();
    this.onClear.emit();
  }

  setAccessibilityErrorAlert() {
    this.alertMessage = this.hideFeedback ? false : this.errorMessage !== "";
  }

  updateErrorMessages() {
    if (!this.formControl?.disabled) {
      this.formControl?.updateValueAndValidity({ emitEvent: false });
    }
    if (
      !!this.formControl &&
      this.formControl instanceof AbstractControl &&
      this.formControl.errors && (this.formControl.touched || this.formControl.dirty)
    ) {
      this.alertMessage = false;
      const validationErrors = this.formControl.errors;
      let buildErrorMessageKey = "";
      if (this.errorKey !== undefined) {
        buildErrorMessageKey = `${this.errorKey}.`;
      }
      this.errorMessage = buildErrorMessageKey + `${Object.keys(validationErrors)[0]}`;
      this.updateAriaDescribedBy(true);
    } else {
      this.errorMessage = "";
      this.alertMessage = false;
      this.updateAriaDescribedBy(false);
    }
    if (this.formControl?.errors) {
      this.updateErrorMessage(this.formControl.errors);
    } else {
      this.removeErrorMessage();
    }
  }

  public updateAriaDescribedBy(showFeedback: boolean) {
    let element = this.getFormElement();
    if (!element) {
        return;
    }

    let describedby: string[] = this.getDescribedBy();

    if (showFeedback) {
        this.handleFeedback(element, describedby);
    } else {
        this.removeFeedback(element, describedby);
    }
  }

  private getDescribedBy(): string[] {
      let describedby: string[] = [];
      if (this.hint) {
          describedby.push(this.hint.id);
      }
      return describedby;
  }

  private handleFeedback(element: any, describedby: string[]) {
      let ariaDescribedBy = element.nativeElement.getAttribute('aria-describedby');
      if ((!ariaDescribedBy && !this.hint) || (this.hint && ariaDescribedBy.split(' ').length < 2)) {
          this.setAccessibilityErrorAlert();
          describedby.push(this.feedbackId);
          this.renderer.setAttribute(element.nativeElement, 'aria-describedby', describedby.join(' '))
          this.renderer.setAttribute(element.nativeElement, 'aria-invalid', 'true');
      }
  }

  private removeFeedback(element: any, describedby: string[]) {
      this.renderer.removeAttribute(element.nativeElement, 'aria-invalid');
      if (this.hint) {
          this.renderer.setAttribute(element.nativeElement, 'aria-describedby', describedby.join(' '))
      } else {
          this.renderer.removeAttribute(element.nativeElement, 'aria-describedby');
      }
  }

  getFormElement(): ElementRef | undefined {
    let element: ElementRef | undefined;
    if (this.inputElement) {
      element = this.inputElement.getElementRef();
    } else if (this.dropdownInput && this.dropdownInput.dropdownTrigger) {
      element = this.dropdownInput.dropdownTrigger;
    } else if (this.comboBoxInput) {
      element = this.comboBoxInput.dropdownTrigger;
    } else if (this.fieldset) {
      element = this.fieldset;
    }
    return element;
  }

}
